import React, { useEffect, useMemo, useState } from 'react'
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  makeStyles
} from '@material-ui/core'
import { AttachMoney, Visibility } from '@material-ui/icons'
import { Redirect } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { useSnackbar } from 'notistack'
import { userProfile } from '@views/profile/state/profileSlice'
import {
  cancelAgentSettlement,
  clearSettlementDetail,
  clearMessages,
  createAgentSettlement,
  loadAgentCommission,
  loadAgents,
  loadSettlementDetail,
  payAgentSettlement,
  selectAgentCommissions
} from './state/agentCommissionsSlice'
import { canManageAgentCommissions, canViewAgentCommissions } from './permissions'
import { caseCountLabel, bogotaPeriod, creationPeriod, money, statusLabel } from './utils'
import CommissionGoalProgress from '@views/shared/CommissionGoalProgress'
import AgentCommissionFilters from './components/AgentCommissionFilters'
import AgentCommissionKpis from './components/AgentCommissionKpis'

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(3),
    maxWidth: 1500,
    margin: '84px auto 0',
    minHeight: 'calc(100vh - 84px)',
    backgroundColor: '#fff'
  },
  title: { fontWeight: 700, color: '#20242a', letterSpacing: 0 },
  actions: {
    position: 'sticky',
    top: 72,
    zIndex: 4,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1.5, 2),
    background: '#fff8f2',
    border: '1px solid #f2b27f',
    borderRadius: 6,
    boxShadow: '0 4px 12px rgba(178, 75, 0, 0.08)'
  },
  actionMeta: { color: '#8b3e08', fontWeight: 700, marginRight: 'auto' },
  history: {
    marginTop: theme.spacing(4),
    paddingTop: theme.spacing(3),
    borderTop: '1px solid #dde1e5'
  },
  sectionTitle: { fontWeight: 700, color: '#2a2f36', marginBottom: theme.spacing(1.5) },
  tableContainer: {
    border: '1px solid #e1e4e8',
    borderRadius: 6,
    boxShadow: '0 2px 8px rgba(31, 38, 46, 0.05)',
    '& .MuiTableCell-head': {
      background: '#f56600',
      color: '#fff',
      fontWeight: 700,
      borderBottom: '1px solid #d95b00'
    },
    '& .MuiTableRow-root:hover': { backgroundColor: '#fafbfc' },
    '& .MuiTableCell-body': { borderBottomColor: '#eceef1', paddingTop: 9, paddingBottom: 9 }
  },
  empty: { padding: theme.spacing(6), textAlign: 'center' },
  '@media (max-width: 600px)': { root: { padding: theme.spacing(2), marginTop: 72 } }
}))

const AgentCommissionsView: React.FC = () => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const { enqueueSnackbar } = useSnackbar()
  const profile = useSelector(userProfile)
  const state = useSelector(selectAgentCommissions)
  const manager = canManageAgentCommissions(profile.role)
  const allowed = canViewAgentCommissions(profile.role)
  const [agentId, setAgentId] = useState('')
  const [period, setPeriod] = useState(bogotaPeriod())
  const [cancelOpen, setCancelOpen] = useState(false)
  const [reason, setReason] = useState('')
  const [confirmAction, setConfirmAction] = useState<'created' | 'goal_not_met' | 'paid' | null>(null)
  const selectedAgent = state.agents.find(agent => agent._id === agentId)
  const minPeriod = manager ? creationPeriod(selectedAgent?.createdAt) : creationPeriod(profile.createdAt)

  useEffect(() => {
    if (manager) dispatch(loadAgents())
  }, [dispatch, manager])
  useEffect(() => {
    if (!allowed || (manager && !agentId) || (minPeriod && period < minPeriod)) return
    dispatch(loadAgentCommission({ period, agentId: manager ? agentId : undefined }))
  }, [agentId, allowed, dispatch, manager, minPeriod, period])
  useEffect(() => {
    if (state.error) {
      enqueueSnackbar(state.error, { variant: 'error' })
      dispatch(clearMessages())
    }
  }, [dispatch, enqueueSnackbar, state.error])
  useEffect(() => {
    if (state.success) {
      enqueueSnackbar(state.success, { variant: 'success' })
      dispatch(clearMessages())
    }
  }, [dispatch, enqueueSnackbar, state.success])
  useEffect(() => {
    if (minPeriod && period < minPeriod) setPeriod(minPeriod)
  }, [minPeriod, period])

  const preview = state.preview
  const active = useMemo(() => state.settlements.find(item => item.period === period && item.status !== 'cancelled'), [
    period,
    state.settlements
  ])
  const payload = { period, agentId: manager ? agentId : undefined }
  const confirm = () => {
    if (confirmAction === 'created' || confirmAction === 'goal_not_met') {
      dispatch(createAgentSettlement({ ...payload, status: confirmAction }))
    } else if (confirmAction === 'paid' && active) {
      dispatch(payAgentSettlement({ ...payload, settlementId: active._id }))
    }
    setConfirmAction(null)
  }
  const cancel = () => {
    if (active && reason.trim()) {
      dispatch(cancelAgentSettlement({ ...payload, settlementId: active._id, reason }))
      setCancelOpen(false)
      setReason('')
    }
  }
  if (!allowed) return <Redirect to="/" />

  return (
    <Box className={classes.root}>
      <Typography className={classes.title} variant="h5" gutterBottom>
        {manager ? 'Comisiones de agentes' : 'Mis comisiones'}
      </Typography>
      <AgentCommissionFilters
        manager={manager}
        agents={state.agents}
        agentId={agentId}
        period={period}
        minPeriod={minPeriod}
        onAgentChange={setAgentId}
        onPeriodChange={setPeriod}
      />
      {manager && !agentId ? (
        <Paper className={classes.empty}>Selecciona un agente para consultar sus comisiones.</Paper>
      ) : state.loading ? (
        <Box className={classes.empty}>
          <CircularProgress />
        </Box>
      ) : preview ? (
        <>
          <CommissionGoalProgress
            currentValue={preview.registeredCaseCount}
            countLabel={`${preview.registeredCaseCount} ${caseCountLabel(preview.period).toLowerCase()}`}
            steps={preview.goalRules.map(rule => ({ value: rule.caseThreshold, label: `Bono ${money(rule.amount)}` }))}
          />
          <AgentCommissionKpis preview={preview} />
          {manager && period < bogotaPeriod() && (
            <Box className={classes.actions}>
              <Typography className={classes.actionMeta}>
                {active ? statusLabel(active.status) : 'Período disponible para cierre'}
              </Typography>
              {!active && preview.commissionAmount > 0 && (
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<AttachMoney />}
                  disabled={state.mutating}
                  onClick={() => setConfirmAction('created')}
                >
                  Liquidar
                </Button>
              )}
              {!active && preview.commissionAmount === 0 && (
                <Button
                  variant="contained"
                  color="primary"
                  disabled={state.mutating}
                  onClick={() => setConfirmAction('goal_not_met')}
                >
                  Cerrar mes sin comisión
                </Button>
              )}
              {active?.status === 'created' && (
                <>
                  <Button
                    variant="contained"
                    color="primary"
                    disabled={state.mutating}
                    onClick={() => setConfirmAction('paid')}
                  >
                    Marcar como pagada
                  </Button>
                  <Button
                    variant="outlined"
                    color="secondary"
                    disabled={state.mutating}
                    onClick={() => setCancelOpen(true)}
                  >
                    Cancelar liquidación
                  </Button>
                </>
              )}
            </Box>
          )}
          <Box className={classes.history}>
            <Typography className={classes.sectionTitle} variant="h6">
              Historial de liquidaciones
            </Typography>
            <TableContainer className={classes.tableContainer} component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Período</TableCell>
                    <TableCell>Casos</TableCell>
                    <TableCell>Promedio</TableCell>
                    <TableCell>Meta</TableCell>
                    <TableCell>Monto</TableCell>
                    <TableCell>Estado</TableCell>
                    <TableCell align="right">Detalle</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {state.settlements.map(item => (
                    <TableRow key={item._id}>
                      <TableCell>{item.period}</TableCell>
                      <TableCell>{item.registeredCaseCount}</TableCell>
                      <TableCell>{item.averageCasesPerBusinessDay.toFixed(1)}</TableCell>
                      <TableCell>{item.goalLevel || '-'}</TableCell>
                      <TableCell>{money(item.commissionAmount)}</TableCell>
                      <TableCell>{statusLabel(item.status)}</TableCell>
                      <TableCell align="right">
                        <IconButton aria-label="Ver detalle" onClick={() => dispatch(loadSettlementDetail(item._id))}>
                          <Visibility />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                  {!state.settlements.length ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        No hay liquidaciones para este agente.
                      </TableCell>
                    </TableRow>
                  ) : null}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </>
      ) : null}
      <Dialog open={cancelOpen} onClose={() => setCancelOpen(false)}>
        <DialogTitle>Cancelar liquidación</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            multiline
            label="Razón"
            value={reason}
            onChange={event => setReason(event.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelOpen(false)}>Volver</Button>
          <Button color="secondary" disabled={!reason.trim() || state.mutating} onClick={cancel}>
            Cancelar liquidación
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={Boolean(confirmAction)} onClose={() => setConfirmAction(null)}>
        <DialogTitle>Confirmar acción</DialogTitle>
        <DialogContent>
          <Typography>
            {confirmAction === 'paid'
              ? '¿Confirmas que el dinero de esta comisión ya fue pagado?'
              : '¿Confirmas el cierre de este período con los valores recalculados por el backend?'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmAction(null)}>Volver</Button>
          <Button color="primary" variant="contained" disabled={state.mutating} onClick={confirm}>
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={Boolean(state.selectedSettlement)}
        onClose={() => dispatch(clearSettlementDetail())}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Detalle de liquidación</DialogTitle>
        <DialogContent>
          {state.selectedSettlement && (
            <>
              <Typography>Agente: {state.selectedSettlement.agentName}</Typography>
              <Typography>Período: {state.selectedSettlement.period}</Typography>
              <Typography>Estado: {statusLabel(state.selectedSettlement.status)}</Typography>
              <Typography>
                {caseCountLabel(state.selectedSettlement.period)}: {state.selectedSettlement.registeredCaseCount}
              </Typography>
              <Typography>Monto: {money(state.selectedSettlement.commissionAmount)}</Typography>
              <Typography variant="subtitle2" style={{ marginTop: 16 }}>
                Personas incluidas ({state.selectedSettlement.personIds.length})
              </Typography>
              <Typography variant="body2" style={{ wordBreak: 'break-all' }}>
                {state.selectedSettlement.personIds.join(', ') || 'Ninguna'}
              </Typography>
              {state.selectedSettlement.cancellationReason && (
                <Typography>Razón: {state.selectedSettlement.cancellationReason}</Typography>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => dispatch(clearSettlementDetail())}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
export default AgentCommissionsView
